import React from "react";
import styled from "styled-components";
import Dropdown from "../../../Generic/Dropdown";
import {
	OfferField,
	OfferTermsBlockField,
	TERMS_CONDITION_TYPE_OPTIONS,
	TermsConditionType,
} from "../../constants";
import { createEmptyTermsBlock } from "../../utils/offerDraft";
import { Section, SectionTitle, Field, FullWidthField, FieldLabel, FieldInput, FieldTextarea, DropdownWrap, ErrorText } from "../shared/OfferFormLayout";

const BlockCard = styled.div`
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 10px;
	padding: 1rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
`;

const BlockHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const ListItemRow = styled.div`
	display: flex;
	gap: 0.5rem;
	align-items: center;
`;

const IconButton = styled.span.attrs({
	className: "material-symbols-outlined",
})`
	cursor: pointer;
	color: ${(props) => (props.$danger ? "red" : "#36395a")};
	user-select: none;
	flex-shrink: 0;
	line-height: 1;
`;

const AddBlockRow = styled.div`
	display: flex;
	justify-content: center;
	width: 100%;
`;

const TermsAndConditionsStep = ({ draft, errors, onChange }) => {
	const blocks = draft[OfferField.TERMS_CONDITION] || [];

	function updateBlocks(next) {
		onChange({ [OfferField.TERMS_CONDITION]: next }, [OfferField.TERMS_CONDITION]);
	}

	function updateBlock(index, updates) {
		updateBlocks(blocks.map((block, i) => (i === index ? { ...block, ...updates } : block)));
	}

	function handleTypeChange(index, type) {
		const content = type === TermsConditionType.ORDERED_LIST ? [""] : "";
		updateBlock(index, {
			[OfferTermsBlockField.TYPE]: type,
			[OfferTermsBlockField.CONTENT]: content,
		});
	}

	function updateListItem(blockIndex, itemIndex, value) {
		const block = blocks[blockIndex];
		const items = Array.isArray(block[OfferTermsBlockField.CONTENT])
			? [...block[OfferTermsBlockField.CONTENT]]
			: [];
		items[itemIndex] = value;
		updateBlock(blockIndex, { [OfferTermsBlockField.CONTENT]: items });
	}

	function addListItem(blockIndex) {
		const block = blocks[blockIndex];
		const items = Array.isArray(block[OfferTermsBlockField.CONTENT])
			? block[OfferTermsBlockField.CONTENT]
			: [];
		updateBlock(blockIndex, { [OfferTermsBlockField.CONTENT]: [...items, ""] });
	}

	function removeListItem(blockIndex, itemIndex) {
		const block = blocks[blockIndex];
		const items = Array.isArray(block[OfferTermsBlockField.CONTENT])
			? block[OfferTermsBlockField.CONTENT]
			: [];
		if (items.length <= 1) {
			updateBlock(blockIndex, { [OfferTermsBlockField.CONTENT]: [""] });
			return;
		}
		updateBlock(blockIndex, {
			[OfferTermsBlockField.CONTENT]: items.filter((_, i) => i !== itemIndex),
		});
	}

	function addBlock() {
		updateBlocks([...blocks, createEmptyTermsBlock()]);
	}

	function removeBlock(index) {
		updateBlocks(blocks.filter((_, i) => i !== index));
	}

	return (
		<Section>
			<SectionTitle>Terms & Conditions</SectionTitle>
			{errors[OfferField.TERMS_CONDITION] ? (
				<ErrorText>{errors[OfferField.TERMS_CONDITION]}</ErrorText>
			) : null}

			{blocks.map((block, index) => (
				<BlockCard key={index} id={`offer-terms-block-${index}`}>
					<BlockHeader>
						<strong>Block {index + 1}</strong>
						<IconButton
							$danger
							role="button"
							aria-label="Remove block"
							onClick={() => removeBlock(index)}>
							delete
						</IconButton>
					</BlockHeader>

					<Field id={`offer-terms-block-${index}-type-field`}>
						<FieldLabel htmlFor={`offer-terms-block-${index}-type`}>Type *</FieldLabel>
						<DropdownWrap id={`offer-terms-block-${index}-type`}>
							<Dropdown
								head="Select type"
								optionsList={TERMS_CONDITION_TYPE_OPTIONS}
								value={block[OfferTermsBlockField.TYPE]}
								onChange={(v) => handleTypeChange(index, v)}
							/>
						</DropdownWrap>
					</Field>

					{block[OfferTermsBlockField.TYPE] === TermsConditionType.PARAGRAPH && (
						<FullWidthField id={`offer-terms-block-${index}-content-field`}>
							<FieldLabel htmlFor={`offer-terms-block-${index}-content`}>
								Content *
							</FieldLabel>
							<FieldTextarea
								id={`offer-terms-block-${index}-content`}
								value={
									typeof block[OfferTermsBlockField.CONTENT] === "string"
										? block[OfferTermsBlockField.CONTENT]
										: ""
								}
								onChange={(e) =>
									updateBlock(index, {
										[OfferTermsBlockField.CONTENT]: e.target.value,
									})
								}
								height="5rem"
								placeholder="Enter paragraph text"
							/>
						</FullWidthField>
					)}

					{block[OfferTermsBlockField.TYPE] === TermsConditionType.ORDERED_LIST && (
						<FullWidthField id={`offer-terms-block-${index}-list-field`}>
							<FieldLabel>List Items *</FieldLabel>
							{(Array.isArray(block[OfferTermsBlockField.CONTENT])
								? block[OfferTermsBlockField.CONTENT]
								: [""]
							).map((item, itemIndex) => (
								<ListItemRow key={itemIndex}>
									<FieldInput
										id={`offer-terms-block-${index}-item-${itemIndex}`}
										value={item}
										onChange={(e) =>
											updateListItem(index, itemIndex, e.target.value)
										}
										placeholder={`Item ${itemIndex + 1}`}
									/>
									<IconButton
										$danger
										role="button"
										aria-label="Remove item"
										onClick={() => removeListItem(index, itemIndex)}>
										delete
									</IconButton>
								</ListItemRow>
							))}
							<IconButton
								role="button"
								aria-label="Add item"
								onClick={() => addListItem(index)}>
								add
							</IconButton>
						</FullWidthField>
					)}
				</BlockCard>
			))}

			<AddBlockRow>
				<IconButton role="button" aria-label="Add block" onClick={addBlock}>
					add
				</IconButton>
			</AddBlockRow>
		</Section>
	);
};

export default TermsAndConditionsStep;
